import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Social } from 'app/containers/Footer/Social'
import { useVocabularyData } from 'app/utils/vocabulary'
import { uniq } from 'lodash'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

export interface Props {
  facebookURL?: string
  images?: ImageProps[]
  languageCode: string
  instagramURL?: string
}

export const SocialWall = memo(function SocialWall({
  facebookURL,
  images,
  languageCode,
  instagramURL,
}: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({
    threshold: 0.25,
    triggerOnce: true,
  })

  return (
    <Container>
      <Head
        className={isAnimObjectVisible ? 'visible' : undefined}
        ref={animObjectRef}
      >
        <Lines />

        <Title text={useVocabularyData('follow-us', languageCode)} />

        <Hashtag>#cabonfadini</Hashtag>

        <Social
          className="social"
          facebookURL={facebookURL}
          instagramURL={instagramURL}
        />
      </Head>

      {images ? (
        <Wrapper dial={5} row wrap>
          {uniq(images)
            .slice(0, 5)
            .map((item, index) => (
              <FadeInUp delay={index * 0.1} key={index} threshold={0.15}>
                <LazyLoadComponent>
                  <Item>
                    <Image {...item} />
                  </Item>
                </LazyLoadComponent>
              </FadeInUp>
            ))}
        </Wrapper>
      ) : null}
    </Container>
  )
})

const Container = styled.section`
  overflow: hidden;
  margin: 7.5rem auto;
  text-align: center;

  .social {
    justify-content: center;
    margin-top: 1.875rem;
    svg {
      fill: ${({ theme }) => theme.colors.variants.primaryDark};
      margin: 0 0.625rem;
    }
  }

  @media (max-width: 1199px) {
    margin: 5.625rem auto 1.875rem;

    .social {
      margin-top: 1.5rem;
    }
  }
`

const Lines = styled.div`
  &:before,
  &:after {
    content: '';
    width: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    top: 3.75rem;
    bottom: calc(100% + 5.375rem);
    transition: 1.4s ease-in-out;
  }
  &:before {
    left: 17.014vw;
  }
  &:after {
    right: 17.014vw;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Head = styled.div`
  position: relative;
  &:before {
    content: '';
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    top: 3.75rem;
    right: 50%;
    left: 50%;
    transition: 1s ease-in-out;
  }
  &.visible {
    &:before {
      right: 17.014vw;
      left: 17.014vw;
    }

    ${Lines} {
      &:before,
      &:after {
        bottom: -3.75rem;
        transition-delay: 1s;
      }
    }
  }

  @media (max-width: 1199px) {
    &:before {
      display: none;
    }
  }
`

const Title = styled(AnimatedTitle)`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.625rem;
  font-weight: 500;
  line-height: 2.375rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1.875rem;
    line-height: 1.875rem;
  }
`

const Hashtag = styled.div`
  display: inline-block;
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  background: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  line-height: 1rem;
  margin-top: 0.9375rem;
  padding: 0 3.1875rem;
  position: relative;
  text-transform: uppercase;
  z-index: 2;

  @media (max-width: 1199px) {
    margin-top: 0.5625rem;
    padding: 0;
  }
`

const Wrapper = styled(FlexBox)`
  margin: 3.75rem -0.972vw 0;
  padding: 0 10vw;

  > div {
    aspect-ratio: 1;
    width: calc(20% - 1.944vw);
    margin: 0 0.972vw;
  }

  @media (max-width: 1199px) {
    margin-top: 2.5rem;
    padding: 0 1.875rem;

    > div {
      opacity: 1 !important;
      transform: none !important;
    }
  }
`

const Item = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
`
